import QrScanner from 'qr-scanner'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Icon } from '@/component/atoms/Icon'
import commonRepo from '@/repository/common.repo'
import modalService from '@/service/modal.service'
import WebviewService from '@/service/webview.service'

import { type CameraStartFailure, classifyCameraStartError } from '../_utils/qrCamera'
import { QR_REJECT_MESSAGE } from '../_utils/qrScoringTarget'
import { parseWorksheetQr, type WorksheetQrQuery } from '../_utils/worksheetQr'
import { openQrRejectModal } from '../QrRejectNotice'
import { decodeQrPhoto } from './decodeQrPhoto'
import S from './QrCameraLayer.style'

// 실기기에서 잰 인식 시간(0.1~5초)에 여유를 둔 값
const NO_RECOGNITION_TIMEOUT_MS = 8000
// 안내를 닫자마자 같은 QR 이 다시 읽혀 안내가 곧바로 또 뜨지 않게 잠시 쉰다
const RESUME_DELAY_MS = 1000
const START_HANG_TIMEOUT_MS = 30000

const FAILURE_HINT: Record<CameraStartFailure, string> = {
  denied: '카메라 권한이 필요합니다.\n브라우저나 기기 설정에서 카메라 권한을 허용해 주세요.',
  'not-found': '카메라를 찾을 수 없습니다.',
  'in-use': '다른 앱이 카메라를 사용하고 있습니다.',
  unsupported: '이 브라우저에서는 카메라를 열 수 없습니다.',
  unknown: '카메라를 열지 못했습니다.',
}
const PHOTO_GUIDE = '학습지를 사진으로 찍거나 앨범에서 골라 주세요.'

// qr-scanner 는 getUserMedia 의 오류를 삼키고 'Camera not found.' 만 던진다. 권한 거부와 구분하려고 한 번 직접 물어본다
const probeCameraFailure = async (scannerError: unknown): Promise<CameraStartFailure> => {
  const classified = classifyCameraStartError(scannerError)
  if (classified !== 'not-found') return classified
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
    stream.getTracks().forEach((track) => track.stop())
    return classified
  } catch (error) {
    return classifyCameraStartError(error)
  }
}

// 직전 시작이 끝나기 전에 다시 시작하면 iPhone Safari 가 AbortError 를 낸다. 레이어를 빠르게
// 닫았다 열거나 개발 모드에서 effect 가 두 번 돌아도 시작이 겹치지 않게 차례로 잇는다
let previousStart: Promise<unknown> = Promise.resolve()

type Props = {
  onClose: () => void
  /** 인식한 뒤에는 멈춰 있다. 레이어를 열어 둔 채 다시 찍게 하려면 resume 을 부른다 */
  onDetect: (query: WorksheetQrQuery, resume: () => void) => void
  /** 레이어 위에 다른 화면이 떠 있는 동안 카메라를 끈다 */
  isSuspended?: boolean
}

const QrCameraLayer = ({ onClose, onDetect, isSuspended = false }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const shutterInputRef = useRef<HTMLInputElement>(null)
  const albumInputRef = useRef<HTMLInputElement>(null)
  // 안내 모달이 떠 있거나 사진을 읽는 동안에는 실시간 인식 결과를 버린다
  const isPausedRef = useRef(false)
  // 사진 인식을 기다리는 사이 레이어가 닫히면 결과로 이동·안내를 띄우지 않는다
  const isClosedRef = useRef(false)
  const isPickingPhotoRef = useRef(false)
  const stopPickerWatchRef = useRef<(() => void) | null>(null)
  const suspendRef = useRef<{ suspend: () => void; resume: () => void } | null>(null)
  const [failure, setFailure] = useState<CameraStartFailure | null>(null)
  // start = 미리보기가 뜨기 전 상한에 걸림, scan = 미리보기 뒤 8초 동안 못 읽음
  const [timedOutKind, setTimedOutKind] = useState<'start' | 'scan' | null>(null)
  // 한 번 보인 셔터는 늦게 미리보기가 떠도 거두지 않는다
  const [hasEverTimedOut, setHasEverTimedOut] = useState(false)
  const [isDecodingPhoto, setIsDecodingPhoto] = useState(false)

  const resumeLater = () => {
    setTimeout(() => {
      isPausedRef.current = false
    }, RESUME_DELAY_MS)
  }

  const handleText = (text: string) => {
    isPausedRef.current = true
    const query = parseWorksheetQr(text)
    if (query) {
      onDetect(query, resumeLater)
      return
    }
    openQrRejectModal({ message: QR_REJECT_MESSAGE['not-worksheet'], onClose: resumeLater })
  }
  const handleTextRef = useRef(handleText)
  useEffect(() => {
    handleTextRef.current = handleText
  })

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (!navigator.mediaDevices?.getUserMedia) {
      setFailure('unsupported')
      return
    }

    let cancelled = false
    const scanner = new QrScanner(
      video,
      (result) => {
        if (isPausedRef.current) return
        handleTextRef.current(result.data)
      },
      {
        preferredCamera: 'environment',
        returnDetailedScanResult: true,
        onDecodeError: () => undefined,
      },
    )

    // 무인식 시간은 미리보기가 실제로 뜬 뒤부터 센다. 처음 권한 창이 떠 있는 동안 세면 허용 직후 실패 안내가 뜬다.
    // 권한 창 대기와 시작이 멈춘 경우는 브라우저·웹뷰마다 구분할 수 없어(권한 조회 미지원) 넉넉한 상한 하나로 둔다.
    // 그 사이에도 앨범 버튼은 늘 있어 사진으로 빠져나갈 수 있다
    const markTimedOut = (kind: 'start' | 'scan') => {
      setTimedOutKind(kind)
      setHasEverTimedOut(true)
    }
    let timer = setTimeout(() => markTimedOut('start'), START_HANG_TIMEOUT_MS)
    // start 가 끝나도 숨은 탭이면 카메라를 열지 않고 끝나, 실제로 영상이 흐르기 시작한 때를 기준으로 삼는다.
    // 다시 세는 건 탭에서 숨었다 돌아온 뒤뿐이다 — 끊겼다 이어지는 재생마다 세면 안내가 끝없이 미뤄진다
    let hasPlayed = false
    let wasHidden = false
    const restartScanTimer = () => {
      wasHidden = false
      clearTimeout(timer)
      setTimedOutKind(null)
      timer = setTimeout(() => markTimedOut('scan'), NO_RECOGNITION_TIMEOUT_MS)
    }
    const armScanTimer = () => {
      if (cancelled) return
      if (hasPlayed && !wasHidden) return
      hasPlayed = true
      restartScanTimer()
    }
    // 숨어 있는 동안은 세지 않고, 돌아오면 재생 이벤트가 다시 오지 않는 환경도 있어 그 자리에서 다시 센다
    const onVisibilityChange = () => {
      if (cancelled || !hasPlayed) return
      if (document.hidden) {
        wasHidden = true
        clearTimeout(timer)
      } else if (!video.paused) {
        restartScanTimer()
      }
    }
    video.addEventListener('playing', armScanTimer)
    document.addEventListener('visibilitychange', onVisibilityChange)
    // 끄는 동안은 숨은 탭처럼 무인식 시간을 세지 않는다. 다시 켜 영상이 흐르면 그때부터 센다
    // 실패 확인(probe)이 권한 창에 묶여 멈춰도 상한 안내가 뜨도록 확인이 끝난 뒤에 거둔다
    const showStartFailure = async (error: unknown) => {
      const failure = await probeCameraFailure(error)
      clearTimeout(timer)
      if (!cancelled) setFailure(failure)
    }
    suspendRef.current = {
      suspend: () => {
        wasHidden = true
        clearTimeout(timer)
        scanner.stop()
      },
      resume: () => {
        if (cancelled) return
        // 다시 켤 때도 시작이 멈출 수 있어 처음과 같은 상한을 건다. 영상이 흐르면 무인식 타이머로 넘어간다
        timer = setTimeout(() => markTimedOut('start'), START_HANG_TIMEOUT_MS)
        void scanner.start().catch(showStartFailure)
      },
    }
    // 권한 창에 답하지 않아 직전 시작이 끝나지 않으면 다음 열기가 영영 막혀, 상한이 지나면 차례를 넘긴다.
    // 닫힌 레이어도 상한까지는 차례를 쥐고 있는다 — 바로 놓으면 멈춘 시작과 겹쳐 AbortError 가 난다
    let isHandedOver = false
    const unblock = new Promise<boolean>((resolve) =>
      setTimeout(() => resolve(true), START_HANG_TIMEOUT_MS),
    )
    const start = Promise.race([previousStart.then(() => false), unblock]).then((handedOver) => {
      isHandedOver = handedOver
      return cancelled ? undefined : scanner.start()
    })
    previousStart = start.catch(() => undefined)
    start.catch(async (error) => {
      if (cancelled) return
      // 차례를 넘겨받아 멈춘 시작과 겹쳐 난 실패는 '다른 앱이 사용 중' 이 아니라 상한 안내 그대로 둔다
      if (isHandedOver && classifyCameraStartError(error) === 'in-use') return
      await showStartFailure(error)
    })

    return () => {
      cancelled = true
      isClosedRef.current = true
      video.removeEventListener('playing', armScanTimer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      clearTimeout(timer)
      scanner.destroy()
    }
  }, [])

  useEffect(() => {
    if (!isSuspended) return
    suspendRef.current?.suspend()
    return () => suspendRef.current?.resume()
  }, [isSuspended])

  // 웹뷰 앱은 웹에서 기기 설정을 여는 bridge 가 없고, 웹은 브라우저 설정으로 바로 보낼 방법이 없어 안내만 한다
  useEffect(() => {
    if (failure !== 'denied') return
    const isApp = WebviewService.isAppAndroid
    modalService.openConfirmModal(
      {
        title: '카메라 권한이 필요합니다',
        content: (
          <>
            QR을 찍으려면 카메라 권한을 허용해 주세요.
            <br />
            {isApp
              ? `설정에서 ${commonRepo.appKorName}의 카메라 권한을 켤 수 있습니다.`
              : '브라우저 주소창의 사이트 설정에서 카메라를 허용해 주세요.'}
          </>
        ),
        hasCancelButton: false,
        actionButtonText: isApp ? '닫기' : '확인',
        onClick: () => modalService.closeModal(),
      },
      // 사진·앨범 입력은 카메라 권한과 무관해, 닫아도 레이어에 남아 사진으로 이어 갈 수 있다
      { hasCloseButton: false },
    )
  }, [failure])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isPausedRef.current) onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  // 선택창이 떠 있는 동안 뒤에서 실시간 인식이 이동시키지 않게 멈춘다. 취소하면 change 가 오지 않아
  // cancel(미지원 브라우저는 창 복귀 focus)로 다시 켠다
  const openPicker = (input: HTMLInputElement | null) => {
    if (!input) return
    isPausedRef.current = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const detach = () => {
      window.removeEventListener('focus', resumeIfCancelled)
      input.removeEventListener('cancel', resumeIfCancelled)
    }
    // 사진을 고르고 돌아올 때도 focus 가 와서, change 가 먼저 오면 예약을 걷는다
    function resumeIfCancelled() {
      detach()
      timer = setTimeout(() => {
        if (!isPickingPhotoRef.current) isPausedRef.current = false
      }, RESUME_DELAY_MS)
    }
    stopPickerWatchRef.current = () => {
      detach()
      clearTimeout(timer)
    }
    window.addEventListener('focus', resumeIfCancelled)
    input.addEventListener('cancel', resumeIfCancelled)
    input.click()
  }

  const onPickPhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    stopPickerWatchRef.current?.()
    isPausedRef.current = true
    isPickingPhotoRef.current = true
    setIsDecodingPhoto(true)
    const text = await decodeQrPhoto(file)
    isPickingPhotoRef.current = false
    if (isClosedRef.current) return
    setIsDecodingPhoto(false)

    if (text === null) {
      openQrRejectModal({ message: QR_REJECT_MESSAGE.unreadable, onClose: resumeLater })
      return
    }
    handleText(text)
  }

  const failureHint = failure
    ? `${FAILURE_HINT[failure]}\n${PHOTO_GUIDE}`
    : timedOutKind
      ? `${timedOutKind === 'scan' ? 'QR이 인식되지 않습니다.' : '카메라가 열리지 않습니다.'}\n${PHOTO_GUIDE}`
      : null
  const showShutter = failure !== null || hasEverTimedOut

  return createPortal(
    <div css={S.layer} role="dialog" aria-label="QR 채점">
      <video ref={videoRef} css={S.video} playsInline muted />
      <header css={S.top}>
        <button type="button" css={S.closeButton} aria-label="닫기" onClick={onClose}>
          <Icon name="close" size={24} color="var(--White)" />
        </button>
        <span>QR 채점</span>
        <span css={S.topSpacer} />
      </header>
      <div css={S.view}>
        <p css={S.guide}>학습지의 QR을 네모 안에 맞춰 주세요</p>
        <div css={S.frame} aria-hidden>
          <span css={[S.corner, S.cornerTopLeft]} />
          <span css={[S.corner, S.cornerTopRight]} />
          <span css={[S.corner, S.cornerBottomLeft]} />
          <span css={[S.corner, S.cornerBottomRight]} />
          {!failureHint && !isDecodingPhoto && <span css={S.scanLine} />}
        </div>
        {isDecodingPhoto ? (
          <p css={[S.hint, S.hintSub]}>사진에서 QR을 읽고 있어요</p>
        ) : failureHint ? (
          <p css={S.hint}>{failureHint}</p>
        ) : (
          <p css={[S.hint, S.hintSub]}>QR을 비추면 채점 화면이 바로 열려요</p>
        )}
      </div>
      <div css={S.bottom}>
        <button
          type="button"
          css={S.albumButton}
          aria-label={isDecodingPhoto ? 'QR 읽는 중' : '앨범에서 선택'}
          disabled={isDecodingPhoto}
          onClick={() => openPicker(albumInputRef.current)}
        >
          <span css={S.albumIcon}>
            <Icon name="photo_album" size={24} color="var(--White)" />
          </span>
        </button>
        {showShutter ? (
          <button
            type="button"
            css={S.shutter}
            aria-label="사진 찍기"
            disabled={isDecodingPhoto}
            onClick={() => openPicker(shutterInputRef.current)}
          >
            <span css={S.shutterInner} />
          </button>
        ) : (
          <span css={S.shutterSpacer} />
        )}
        <span css={S.albumSpacer} />
        <input
          ref={shutterInputRef}
          css={S.fileInput}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onPickPhoto}
        />
        <input
          ref={albumInputRef}
          css={S.fileInput}
          type="file"
          accept="image/*"
          onChange={onPickPhoto}
        />
      </div>
    </div>,
    document.body,
  )
}

export default QrCameraLayer
